import { formatDistanceToNow } from 'date-fns';
import { ActivityTypeBadge } from '../ui/Badge.js';
import { SkeletonRow } from '../ui/Skeleton.js';
import { cn } from '../../lib/cn.js';

interface ActivityEvent {
  id: number;
  landlordId: number;
  landlordName?: string | null;
  userId: number | null;
  userName: string | null;
  type: string;
  apiType?: string | null;
  detail: Record<string, unknown> | null;
  createdAt: string;
}

interface ActivityFeedProps {
  events: ActivityEvent[];
  isLoading?: boolean;
  showLandlord?: boolean;
  onLandlordClick?: (landlordId: number) => void;
}

function activityDescription(ev: ActivityEvent): string {
  const apiType = ev.detail?.api_type ?? ev.apiType;
  if (ev.type === 'call') {
    if (apiType === 'call_answered') return 'Called — answered';
    if (apiType === 'call_no_answer') return 'Called — no answer';
    return 'Called';
  }
  if (ev.type === 'whatsapp') return 'WhatsApp sent';
  if (ev.type === 'note' && ev.detail?.note) return String(ev.detail.note);
  if (ev.type === 'stage_change') {
    const from = ev.detail?.stage_from;
    const to = ev.detail?.stage_to;
    if (from && to) return `Stage: ${from} → ${to}`;
    return 'Stage changed';
  }
  if (ev.type === 'assignment') {
    const action = ev.detail?.action;
    if (action === 'assigned') return `Assigned to ${ev.detail?.agent_name ?? 'agent'}`;
    if (action === 'unassigned') return 'Unassigned';
    return 'Assignment changed';
  }
  if (ev.type === 'edit') {
    if (ev.detail?.action === 'tag_added') return `Tag added: ${ev.detail.tag_name}`;
    if (ev.detail?.action === 'tag_removed') return 'Tag removed';
    return 'Record edited';
  }
  return ev.type;
}

export function ActivityFeed({
  events,
  isLoading,
  showLandlord = false,
  onLandlordClick,
}: ActivityFeedProps) {
  if (isLoading) {
    return (
      <div>
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <p className="text-center text-secondary text-sm py-8">No activity yet</p>
    );
  }

  return (
    <div className="divide-y divide-border">
      {events.map((ev) => (
        <div key={ev.id} className="flex items-start gap-3 px-4 py-3">
          <ActivityTypeBadge type={ev.type} />
          <div className="flex-1 min-w-0">
            {showLandlord && ev.landlordName && (
              <button
                onClick={() => onLandlordClick?.(ev.landlordId)}
                className={cn(
                  'text-sm font-medium text-primary-text hover:text-primary truncate block w-full text-left',
                  onLandlordClick && 'cursor-pointer'
                )}
              >
                {ev.landlordName}
              </button>
            )}
            <p className="text-sm text-primary-text">{activityDescription(ev)}</p>
            <p className="text-xs text-secondary mt-0.5">
              {ev.userName ?? 'System'} · {formatDistanceToNow(new Date(ev.createdAt), { addSuffix: true })}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
